/**
 * BranchInfraStatus
 * Shows 4 live infrastructure indicator cards for a branch in edit mode:
 *   1. Base de Datos (Dokploy postgres service)
 *   2. Backend Flask (Dokploy application service)
 *   3. DNS (IONOS registration + resolution)
 *   4. Salud del Servicio (branch /health endpoint)
 *
 * Fetches from GET /api/v1/provisioning/<branch_id>/status
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, Database, Server, Globe, Activity, AlertCircle, Zap, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  getBranchInfraStatus,
  getBranchInfraRecord,
  runProvisioning,
  type InfraStatus,
  type ServiceCheck,
  type DnsCheck,
  type HealthCheck,
} from "@/services/infraService";

// ─── Badge helpers ────────────────────────────────────────────────────────────

type Level = "ok" | "warn" | "error" | "unknown";

const levelClass: Record<Level, string> = {
  ok:      "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30",
  warn:    "bg-amber-500/20  text-amber-400  border border-amber-500/30",
  error:   "bg-red-500/20    text-red-400    border border-red-500/30",
  unknown: "bg-white/10      text-white/40   border border-white/15",
};

function StatusBadge({ level, label }: { level: Level; label: string }) {
  return (
    <span className={cn("flex-shrink-0 whitespace-nowrap text-[10px] font-semibold px-2 py-0.5 rounded-full", levelClass[level])}>
      {label}
    </span>
  );
}

// ─── Individual cards ─────────────────────────────────────────────────────────

function serviceLevel(check: ServiceCheck): Level {
  if (!check.exists) return "unknown";
  const s = check.status?.toLowerCase() ?? "";
  if (s === "running" || s === "done") return "ok";
  if (s === "error")                   return "error";
  if (s === "idle")                    return "warn";
  return "unknown";
}

function serviceLabel(check: ServiceCheck): string {
  if (!check.exists) return "No aprovisionado";
  const s = check.status?.toLowerCase() ?? "";
  if (s === "running") return "Activo";
  if (s === "done")    return "Listo";
  if (s === "error")   return "Error";
  if (s === "idle")    return "Inactivo";
  return check.status ?? "Desconocido";
}

function DbCard({ check }: { check: ServiceCheck }) {
  return (
    <InfraCard
      icon={<Database className="h-4 w-4" />}
      title="Base de Datos"
      level={serviceLevel(check)}
      badge={serviceLabel(check)}
      detail={
        check.exists
          ? `${check.app_name ?? "—"}  ·  DB: ${check.database_name ?? "—"}`
          : "Sin registro en Dokploy"
      }
    />
  );
}

function BackendCard({ check }: { check: ServiceCheck }) {
  return (
    <InfraCard
      icon={<Server className="h-4 w-4" />}
      title="Backend Flask"
      level={serviceLevel(check)}
      badge={serviceLabel(check)}
      detail={check.exists ? (check.app_name ?? "—") : "Sin registro en Dokploy"}
    />
  );
}

function DnsCard({ check, healthReachable }: { check: DnsCheck; healthReachable?: boolean }) {
  // DNS is considered active if the health endpoint responds (even via HTTP),
  // because that means the domain resolves and routes to the right server.
  let level: Level;
  let label: string;
  if (healthReachable) {
    level = "ok";    label = "Activo";
  } else if (check.requested) {
    level = "warn"; label = "Pendiente";
  } else {
    level = "unknown"; label = "Sin solicitar";
  }

  const detail = check.resolves
    ? `Resuelve → ${check.resolved_ip}`
    : check.subdomain;

  return (
    <InfraCard
      icon={<Globe className="h-4 w-4" />}
      title="DNS"
      level={level}
      badge={label}
      detail={detail}
    />
  );
}

function HealthCard({ check, backendUrl }: { check: HealthCheck; backendUrl: string }) {
  let level: Level = "unknown";
  let label = backendUrl ? "Sin respuesta" : "Sin URL";
  if (check.reachable && check.protocol === "https") { level = "ok";   label = "HTTPS ✓"; }
  else if (check.reachable && check.protocol === "http")  { level = "warn"; label = "HTTP (sin TLS)"; }
  else if (check.reachable)                               { level = "ok";   label = "Respondiendo"; }
  else if (check.error === "ssl_error")  { level = "warn";  label = "Sin certificado"; }
  else if (check.error === "timeout")    { level = "warn";  label = "Timeout"; }
  else if (check.error === "connection_refused") { level = "error"; label = "Sin conexión"; }
  else if (backendUrl && !check.reachable) { level = "error"; label = "Sin respuesta"; }

  const branchUuid = (check.response as Record<string, string> | undefined)?.branch_uuid;
  const detail = check.reachable
    ? `${check.latency_ms}ms${branchUuid ? ` · ${branchUuid.substring(0, 8)}…` : ""}`
    : (check.error ?? "—");

  return (
    <InfraCard
      icon={<Activity className="h-4 w-4" />}
      title="Salud del Servicio"
      level={level}
      badge={label}
      detail={detail}
    />
  );
}

// ─── Base card ────────────────────────────────────────────────────────────────

function InfraCard({
  icon,
  title,
  level,
  badge,
  detail,
}: {
  icon: React.ReactNode;
  title: string;
  level: Level;
  badge: string;
  detail: string;
}) {
  const borderClass: Record<Level, string> = {
    ok:      "border-emerald-500/20",
    warn:    "border-amber-500/20",
    error:   "border-red-500/20",
    unknown: "border-white/10",
  };

  return (
    <div className={cn(
      "rounded-xl border bg-white/[0.03] p-3 flex items-start gap-3 overflow-hidden min-w-0",
      borderClass[level]
    )}>
      <div className={cn("mt-0.5 flex-shrink-0", {
        "text-emerald-400": level === "ok",
        "text-amber-400":   level === "warn",
        "text-red-400":     level === "error",
        "text-white/30":    level === "unknown",
      })}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-xs font-medium text-white/60 truncate">{title}</p>
          <StatusBadge level={level} label={badge} />
        </div>
        <p className="text-[11px] text-white/35 truncate">{detail}</p>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface BranchInfraStatusProps {
  branchId: string;
}

export default function BranchInfraStatus({ branchId }: BranchInfraStatusProps) {
  const [status, setStatus]         = useState<InfraStatus | null>(null);
  const [loading, setLoading]       = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const pollRef                     = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getBranchInfraStatus(branchId);
      setStatus(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al verificar infraestructura");
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  // Poll the stored record (fast) every 5s while provisioning_status is "running"
  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const record = await getBranchInfraRecord(branchId);
        const ps = record?.provisioning_status;
        if (ps === "done" || ps === "failed") {
          stopPolling();
          setProvisioning(false);
          fetchStatus(); // full refresh with live checks
        }
      } catch { /* ignore */ }
    }, 5000);
  }, [branchId, fetchStatus]);

  // Auto-fetch when component mounts
  useEffect(() => {
    fetchStatus();
    return () => stopPolling();
  }, [fetchStatus]);

  // If current record is already "running" when panel opens, start polling
  useEffect(() => {
    if (status?.infra?.provisioning_status === "running") {
      startPolling();
    }
  }, [status?.infra?.provisioning_status, startPolling]);

  const handleProvision = async () => {
    setProvisioning(true);
    setError(null);
    try {
      await runProvisioning(branchId);
      startPolling();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al iniciar el aprovisionamiento");
      setProvisioning(false);
    }
  };

  const provStatus = status?.infra?.provisioning_status;
  const isRunning  = provStatus === "running" || provisioning;
  const canProvision = status !== null && !isRunning &&
    (provStatus === undefined || provStatus === null ||
     provStatus === "not_provisioned" || provStatus === "failed");

  return (
    <section className="space-y-3 min-w-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
          Infraestructura
        </p>
        <div className="flex items-center gap-1">
          {canProvision && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleProvision}
              disabled={isRunning || loading}
              className="h-6 px-2 text-[10px] text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 gap-1"
            >
              <Zap className="h-3 w-3" />
              Provisionar
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchStatus}
            disabled={loading || isRunning}
            className="h-6 w-6 text-white/30 hover:text-white/70 hover:bg-white/10"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/5 px-3 py-2">
          <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* Running state */}
      {isRunning && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3">
          <Loader2 className="h-4 w-4 text-amber-400 animate-spin flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-amber-300">Aprovisionando entorno…</p>
            <p className="text-[11px] text-amber-400/60 mt-0.5">Esto puede tardar 2–5 minutos. Esta pantalla se actualizará automáticamente.</p>
          </div>
        </div>
      )}

      {/* Skeleton while loading for the first time */}
      {loading && !status && !isRunning && (
        <div className="grid grid-cols-1 gap-2">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-3 h-[58px] animate-pulse"
            />
          ))}
        </div>
      )}

      {/* Cards — hidden while running to avoid confusion */}
      {status && !isRunning && (
        <div className="grid grid-cols-1 gap-2">
          <DbCard      check={status.checks.database} />
          <BackendCard check={status.checks.backend} />
          <DnsCard     check={status.checks.dns} healthReachable={status.checks.health.reachable} />
          <HealthCard  check={status.checks.health} backendUrl={status.backend_url} />
        </div>
      )}

      {/* Backend URL hint */}
      {status?.backend_url && !isRunning && (
        <p className="w-full text-[10px] text-white/25 truncate px-0.5">
          {status.backend_url}
        </p>
      )}
    </section>
  );
}
